import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '@/store/hooks';
import { projectMemberAPI } from '@/api/projectMemberAPI';

/**
 * Project-level permission helper based on membership role + global role.
 *
 * - Global admin/super_admin: full access across projects
 * - Project roles:
 *   - admin / member: can edit within that project
 *   - viewer: read-only within that project
 *
 * Resolution order for the project role:
 *   1. `project.my_role` already loaded in the Redux project list (no API call)
 *   2. Fallback: `checkMembership` API call (only if the project isn't in the
 *      store, e.g. deep-linked before /projects has loaded)
 */
export function useProjectPermissions(projectId?: string | null) {
  const { user: currentUser } = useAppSelector((state) => state.auth);
  const projectsList = useAppSelector((state) => state.projects.projects);

  // Prefer the role embedded in the projects API response. This is set on
  // every page that loads /projects, which means we almost never hit the API.
  const roleFromStore = useMemo(() => {
    if (!projectId) return null;
    const p = projectsList.find((proj: any) => proj?.id === projectId);
    return (p as any)?.my_role ?? null;
  }, [projectsList, projectId]);

  const [fetchedRole, setFetchedRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const loadMembership = useCallback(async () => {
    // Skip the API call when we already have the role from the store, or the
    // project isn't relevant. This is the hot path that used to make N calls.
    if (!projectId || !currentUser?.id || roleFromStore !== null) {
      setFetchedRole(null);
      return;
    }

    try {
      setLoading(true);
      const data = await projectMemberAPI.checkMembership(projectId, currentUser.id);
      const role =
        (data as any)?.member?.role ??
        (data as any)?.role ??
        null;
      setFetchedRole(role);
    } catch (error) {
      console.error('Failed to load project membership:', error);
      setFetchedRole(null);
    } finally {
      setLoading(false);
    }
  }, [projectId, currentUser?.id, roleFromStore]);

  useEffect(() => {
    void loadMembership();
  }, [loadMembership]);

  const memberRole = roleFromStore ?? fetchedRole;

  const {
    isGlobalAdmin,
    hasWriteAccess,
    isViewerOnly,
  } = useMemo(() => {
    const mainRole = currentUser?.role?.toLowerCase();
    const projectRole = memberRole?.toLowerCase();

    const isAdminLike = mainRole === 'admin' || mainRole === 'super_admin';
    const viewerOnly = !isAdminLike && projectRole === 'viewer';
    const write =
      isAdminLike ||
      projectRole === 'admin' ||
      projectRole === 'member';

    return {
      isGlobalAdmin: !!isAdminLike,
      hasWriteAccess: !!write,
      isViewerOnly: !!viewerOnly,
    };
  }, [currentUser?.role, memberRole]);

  return {
    loading,
    memberRole,
    isGlobalAdmin,
    hasWriteAccess,
    isViewerOnly,
  };
}


